import { Formik } from "formik";
import moment from "moment-timezone/builds/moment-timezone-with-data-10-year-range.js";
import React from "react";
import { View } from "react-native";
import { updateEventDateTimeDetails } from "../http/events";
import { EventDateTimeSchema } from "../screens/Schemas";
import AppDatePicker from "../shared/AppDatePicker";
import MultiDaySelector from "./MultiDaySelector";
import TimezonePicker from "./TimezonePicker";
import { notify } from "./AppToast";
import Button, { ButtonRow } from "@/components/settings/Button";
import { UnsavedChanges } from "@/components/settings/SaveStatus";
import tw from "twrnc";
import { t } from "@/localization";

// Date, time, timezone and days. Not auto-saved: rescheduling moves reminder
// emails and the streaming window (and extra days can cost credits), so it
// waits for "Save date and time".
const EventDateTimeForm = ({ event }) => {
  const { slug, event_datetime_utc, event_timezone, days = 1 } = event;

  //Convert back to local date & time for editing:
  const event_datetime = new Date(
    moment
      .tz(new Date(event_datetime_utc), event_timezone)
      .format("YYYY-MM-DDTHH:mm:00")
  );

  return (
    <Formik
      initialValues={{
        event_datetime,
        event_timezone,
        days,
      }}
      onSubmit={async (values, actions) => {
        actions.setSubmitting(true);
        try {
          const updated = await updateEventDateTimeDetails({ slug, ...values });
          actions.resetForm({ values: updated });
          notify.success(
            t("Event rescheduled"),
            t("Guests will see the new date.")
          );
        } catch (err) {
          console.error(err);
          actions.setSubmitting(false);
          notify.error(
            t("Couldn't change the date"),
            String(err?.message || err)
          );
        }
      }}
      validationSchema={EventDateTimeSchema}
    >
      {(f) => (
        <View>
          <UnsavedChanges dirty={f.dirty} />
          <AppDatePicker
            selected={f.values.event_datetime}
            selectedValue={f.values.event_datetime}
            onChange={(value) => {
              f.setFieldValue("event_datetime", value);
            }}
            popperPlacement="top"
            onBlur={f.handleBlur("event_datetime")}
            placeholderText={t("Event date and time")}
            iconName="calendar-outline"
            errorMessage={
              f.errors.event_datetime
                ? t("Choose the event's local date and time")
                : null
            }
          />

          <TimezonePicker
            value={f.values.event_timezone}
            onChange={f.handleChange("event_timezone")}
            align="left"
            helpTopic="details.timezone"
          />

          <MultiDaySelector
            f={f}
            align="left"
            helpTopic="details.days"
            linkText={t("Make it a multi-day event")}
          />

          {f.dirty && (
            <ButtonRow style={tw`mt-3`}>
              <Button
                text={t("Cancel")}
                variant="tertiary"
                size="md"
                fullWidth={false}
                disabled={f.isSubmitting}
                onPress={() => f.resetForm()}
              />
              <Button
                text={t("Save date and time")}
                variant="primary"
                size="md"
                fullWidth={false}
                loading={f.isSubmitting}
                onPress={() => f.submitForm()}
              />
            </ButtonRow>
          )}
        </View>
      )}
    </Formik>
  );
};
export default EventDateTimeForm;
